import { useEffect, useMemo, useState } from 'react';
import useVisibleElement from './useVisibleElement';

type UseInfiniteScrollOptions = {
  onReachEnd?: () => void;
  onReachStart?: () => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  scroll?: boolean;
  options?: IntersectionObserverInit;
  scrollContainer?: HTMLElement | null;
};

type UseInfiniteScrollReturn = {
  startSentinelRef: (node: HTMLElement | null) => void;
  endSentinelRef: (node: HTMLElement | null) => void;
  scrollContainerRef: (node: HTMLElement | null) => void;
};

export default function useInfiniteScroll({
  onReachEnd,
  onReachStart,
  hasNextPage,
  hasPreviousPage,
  scroll,
  options,
  scrollContainer,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn {
  const [internalScrollContainer, setInternalScrollContainer] =
    useState<HTMLElement | null>(null);

  const effectiveScrollContainer = scrollContainer ?? internalScrollContainer;

  const intersectionOptions: IntersectionObserverInit = useMemo(
    () => ({
      root: scroll ? effectiveScrollContainer : undefined,
      rootMargin: '100px',
      ...options,
    }),
    [scroll, effectiveScrollContainer, options],
  );

  const { intersecting: endIntersecting, setRef: setEndRef } =
    useVisibleElement(intersectionOptions);

  const { intersecting: startIntersecting, setRef: setStartRef } =
    useVisibleElement(intersectionOptions);

  useEffect(() => {
    if (endIntersecting && hasNextPage) onReachEnd?.();
  }, [endIntersecting, hasNextPage, onReachEnd]);

  useEffect(() => {
    if (startIntersecting && hasPreviousPage) onReachStart?.();
  }, [startIntersecting, hasPreviousPage, onReachStart]);

  return {
    startSentinelRef: setStartRef,
    endSentinelRef: setEndRef,
    scrollContainerRef: setInternalScrollContainer,
  };
}
